import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { navCategories, dailyArticles } from '@/mocks/home';
import { allArticles, categorySlugMap } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = allArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar categories={navCategories} />
        <main className="pt-20 md:pt-24 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-article-line text-3xl"></i>
            </div>
            <h2 className="mt-4 text-xl font-bold text-foreground-800">文章未找到</h2>
            <p className="mt-2 text-foreground-500 text-sm">该文章可能已被删除或地址不正确</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-6 bg-foreground-950 text-background-50 px-6 py-3 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = allArticles
    .filter((a) => a.slug !== slug && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  if (relatedArticles.length < 3) {
    const others = allArticles
      .filter((a) => a.slug !== slug && a.categorySlug !== article.categorySlug)
      .slice(0, 3 - relatedArticles.length);
    relatedArticles.push(...others);
  }

  return (
    <>
      <Navbar categories={navCategories} />
      <main className="pt-20 md:pt-24">
        <article className="max-w-[1000px] mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">
              {article.category}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
          </nav>

          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                {article.category}
              </span>
              {article.hot && (
                <span className="px-3 py-1 rounded-full bg-foreground-950 text-background-50 text-xs font-medium flex items-center gap-1">
                  <i className="ri-fire-line text-[10px]"></i>
                  热门
                </span>
              )}
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-black text-foreground-950 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 mt-5 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {article.views} 阅读
              </span>
            </div>
          </header>

          <figure className="relative rounded-2xl overflow-hidden aspect-[21/9] mb-10">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </figure>

          <div
            className="prose prose-lg max-w-none
              prose-headings:font-heading prose-headings:font-bold prose-headings:text-foreground-950
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-base md:prose-p:text-lg
              prose-ul:text-foreground-700
              prose-li:marker:text-primary-500
              prose-strong:text-foreground-900
              prose-a:text-primary-500 prose-a:no-underline hover:prose-a:underline
              mb-12
            "
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex items-center justify-between py-6 border-t border-background-200">
            <div className="flex gap-2">
              {['ri-wechat-line', 'ri-weibo-line', 'ri-link'].map((icon) => (
                <button
                  key={icon}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-600 transition-colors cursor-pointer"
                  aria-label="分享"
                >
                  <i className={`${icon} text-lg`}></i>
                </button>
              ))}
            </div>
            <Link
              to="/"
              className="text-sm text-foreground-500 hover:text-primary-500 transition-colors flex items-center gap-1"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-[1000px] mx-auto px-4 md:px-6 pb-16">
            <div className="border-t border-background-200 pt-12">
              <h2 className="text-2xl font-heading font-black text-foreground-950 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to="/category/all"
                    className="group rounded-2xl overflow-hidden bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <span className="text-xs text-primary-500 font-medium">{ra.category}</span>
                      <h3 className="mt-1 text-sm font-bold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-500">{ra.date} · {ra.views} 阅读</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.image,
            datePublished: article.date,
            dateModified: article.date,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            publisher: {
              '@type': 'Organization',
              name: '今日看料',
              url: 'http://hlw51.cn/',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `http://hlw51.cn/article/${article.slug}`,
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'http://hlw51.cn/' },
                { '@type': 'ListItem', position: 2, name: article.category, item: `http://hlw51.cn/category/${article.categorySlug}` },
                { '@type': 'ListItem', position: 3, name: article.title, item: `http://hlw51.cn/article/${article.slug}` },
              ],
            },
          }),
        }}
      />
    </>
  );
}